import { useParams, Link } from "react-router-dom";
import { CalendarDays, Timer, ArrowLeft } from "lucide-react";
import { getPost, posts } from "@/data/blog";
import { images } from "@/config/site";
import { SEO, articleLd, breadcrumbLd } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { CTA } from "@/components/CTA";
import NotFound from "@/pages/NotFound";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <NotFound />;
  const img = images[post.image];
  const related = posts.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <>
      <SEO
        title={`${post.title} | North Ember Painting`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        type="article"
        image={img.url}
        jsonLd={[
          articleLd(post, img.url),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Painting Advice", path: "/blog" },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
        ]}
      />
      <article className="bg-alabaster" data-testid="blog-article">
        <header className="relative overflow-hidden bg-ink">
          <img src={img.url} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/40" aria-hidden="true" />
          <div className="relative mx-auto max-w-3xl px-4 py-20 sm:px-6 md:py-28">
            <Reveal>
              <Link to="/blog" data-testid="article-back-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-300 transition-colors duration-200 hover:text-ember">
                <ArrowLeft className="h-4 w-4" aria-hidden="true" /> All articles
              </Link>
              <p className="mt-6 text-xs font-semibold uppercase tracking-[0.25em] text-ember">{post.category}</p>
              <h1 className="mt-3 font-display text-3xl font-extrabold leading-[1.12] tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
                {post.title}
              </h1>
              <div className="mt-5 flex items-center gap-5 text-sm text-slate-400">
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays className="h-4 w-4" aria-hidden="true" />
                  {new Date(post.date + "T12:00:00").toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Timer className="h-4 w-4" aria-hidden="true" />
                  {post.readTime}
                </span>
              </div>
            </Reveal>
          </div>
        </header>

        <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6">
          <Reveal>
            <div className="space-y-6">
              {post.blocks.map((b, i) => {
                if (b.t === "h2")
                  return (
                    <h2 key={i} className="pt-4 font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                      {b.text}
                    </h2>
                  );
                if (b.t === "list")
                  return (
                    <ul key={i} className="space-y-2.5">
                      {b.items.map((item) => (
                        <li key={item} className="flex items-start gap-3 text-base leading-relaxed text-slate-600">
                          <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-ember" aria-hidden="true" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  );
                return <p key={i} className="text-base leading-relaxed text-slate-600">{b.text}</p>;
              })}
            </div>
            <p className="mt-8 border-t border-slate-200 pt-6 text-xs italic text-slate-400">
              Photography in this article is illustrative concept imagery.
            </p>
          </Reveal>

          <Reveal className="mt-14">
            <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900">Keep reading</h2>
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  data-testid={`related-post-${r.slug}`}
                  className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 hover:border-ember/40 hover:shadow-lg"
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-ember">{r.category}</p>
                  <h3 className="mt-2 font-display text-lg font-semibold text-slate-900">{r.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{r.excerpt}</p>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </article>
      <CTA title="Have a project in mind after reading?" />
    </>
  );
}
